import { Box, Flex, Text, Heading, Badge } from "@chakra-ui/react";
import { useState } from "react";
import { AppButton, AppTable, TableRow, TableCell, AppSelect, AppModal } from "../../components";
import { colors } from "../../theme";
import { useMalls, useSubscriptionsOverview } from "../../services/useQueries";
import { formatCount, formatDate, formatOMR } from "../../utils/format";
import SubscriptionPanel from "../RestaurantManagementScreen/SubscriptionPanel";

// Where each shop stands (worked out by the server).
const STATE = {
  ACTIVE: { label: "Active", palette: "green" },
  ENDING: { label: "Cancelled, runs to its end date", palette: "orange" },
  PAST_DUE: { label: "Renewal payment failed", palette: "red" },
  ENDED: { label: "Ended", palette: "gray" },
  NONE: { label: "No plan yet", palette: "gray" },
};
const INTERVAL = { MONTHLY: "month", YEARLY: "year" };
const HEADERS = ["Shop", "Plan", "Status", "Runs until", "Listing"];

const planLine = (plan) =>
  plan ? `${plan.name} · ${formatOMR(plan.price)}${INTERVAL[plan.interval] ? ` a ${INTERVAL[plan.interval]}` : ""}` : "—";

const endLine = (row) => {
  if (row.daysLeft !== null && row.endDate) {
    return `${formatDate(row.endDate)} (${row.daysLeft} ${row.daysLeft === 1 ? "day" : "days"} left)`;
  }
  if (row.endDate) return `Ended ${formatDate(row.endDate)}`;
  return "—";
};

const listing = (row) => {
  if (row.blocked) return { label: "Blocked", palette: "red" };
  if (row.approvalStatus === "PENDING") return { label: "Waiting for approval", palette: "orange" };
  if (row.approvalStatus === "REJECTED") return { label: "Not approved", palette: "red" };
  return { label: "Approved", palette: "green" };
};

/**
 * Every shop's subscription in one list, for the super admin: which plans run
 * out soon, which renewals failed, which shops never started one. A row opens
 * the same controls as the shop's details (start a plan, move the end date).
 */
const SubscriptionsScreen = () => {
  const [state, setState] = useState("");
  const [mallId, setMallId] = useState();
  const [openShop, setOpenShop] = useState(null);
  const { data, isLoading, isError } = useSubscriptionsOverview({
    ...(state ? { state } : {}),
    ...(mallId ? { mallId } : {}),
  });
  const { data: mallsResponse } = useMalls();
  const malls = Array.isArray(mallsResponse) ? mallsResponse : mallsResponse?.data || mallsResponse?.items || [];

  const rows = data?.data || [];
  const counts = data?.counts || {};
  const soonDays = data?.endsSoonDays ?? 14;
  const tabs = [
    { value: "", label: "All", count: "ALL" },
    { value: "ENDS_SOON", label: `Ending within ${soonDays} days`, count: "ENDS_SOON" },
    { value: "PAST_DUE", label: "Payment failed", count: "PAST_DUE" },
    { value: "ENDING", label: "Cancelled by the shop", count: "ENDING" },
    { value: "ENDED", label: "Ended", count: "ENDED" },
    { value: "NONE", label: "No plan yet", count: "NONE" },
  ];

  const renderRow = (row) => {
    const shown = STATE[row.state] || { label: row.state, palette: "gray" };
    const listed = listing(row);
    return (
      <TableRow key={row.restaurantId} onClick={() => setOpenShop(row)}>
        <TableCell>
          <Text fontWeight="600">{row.name || "Unnamed shop"}</Text>
          <Text fontSize="xs" color={colors.textSecondary}>
            {[row.mall?.name, row.email].filter(Boolean).join(" · ")}
          </Text>
        </TableCell>
        <TableCell>{planLine(row.plan)}</TableCell>
        <TableCell>
          <Badge colorPalette={shown.palette} px={2} py={1} borderRadius="full" fontSize="xs" whiteSpace="nowrap">
            {shown.label}
          </Badge>
        </TableCell>
        <TableCell whiteSpace="nowrap">{endLine(row)}</TableCell>
        <TableCell>
          <Badge colorPalette={listed.palette} variant="outline" px={2} py={1} borderRadius="full" fontSize="xs" whiteSpace="nowrap">
            {listed.label}
          </Badge>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <Flex justify="space-between" align="flex-start" mb={6} gap={4} flexWrap="wrap">
        <Box>
          <Heading size="lg" color={colors.text} mb={1}>
            Subscriptions
          </Heading>
          <Text color={colors.textSecondary}>
            Every shop's plan. Buyers only see shops with a running plan that are approved and not blocked.
          </Text>
        </Box>
        <AppSelect
          placeholder="All malls"
          options={malls.map((m) => ({ value: m.id, label: m.name }))}
          value={mallId}
          onChange={setMallId}
          width="220px"
        />
      </Flex>

      <Flex gap={2} mb={6} flexWrap="wrap">
        {tabs.map((tab) => (
          <AppButton
            key={tab.value || "all"}
            variant={state === tab.value ? "primary" : "secondary"}
            onClick={() => setState(tab.value)}
          >
            {tab.label}
            {counts[tab.count] !== undefined ? ` (${formatCount(counts[tab.count])})` : ""}
          </AppButton>
        ))}
      </Flex>

      <AppTable
        headers={HEADERS}
        data={rows}
        renderRow={renderRow}
        isLoading={isLoading}
        emptyMessage={isError ? "Couldn't load subscriptions. Check the connection and try again." : "No shops here."}
      />

      <AppModal
        isOpen={!!openShop}
        onClose={() => setOpenShop(null)}
        title={openShop?.name || "Shop"}
        size="md"
        footer={
          <Flex justify="flex-end" w="100%">
            <AppButton variant="secondary" onClick={() => setOpenShop(null)}>
              Close
            </AppButton>
          </Flex>
        }
      >
        {openShop && <SubscriptionPanel restaurantId={openShop.restaurantId} />}
      </AppModal>
    </Box>
  );
};

export default SubscriptionsScreen;
